import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './CartPage.css';
import UserLayout from './UserLayout';
import LoadingButton from './LoadingButton';
import { resolveImageUrl } from '../utils/image';
import { updatePageSEO } from '../utils/seo';
import StatutoryComplianceNotice from './StatutoryComplianceNotice';

export default function CartPage({ user, setUser }: { user: any, setUser: (user: any) => void }) {
  const { cart, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [updatingQuantity, setUpdatingQuantity] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    updatePageSEO({
      title: 'Shopping Cart | Jai Bhavani Fireworks',
      description: 'Review your festive firecracker wholesale cart. Secure booking and fast WhatsApp confirmation from Jai Bhavani Fireworks.',
      canonicalPath: '/cart'
    });
  }, []);

  const MIN_ORDER_VALUE = 2000;
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalSavings = subtotal; // 50% discount savings equivalent
  const isMinOrderMet = subtotal >= MIN_ORDER_VALUE;
  const amountNeeded = Math.max(0, MIN_ORDER_VALUE - subtotal);
  const progressPercent = Math.min(100, Math.round((subtotal / MIN_ORDER_VALUE) * 100));

  const handleQuantityUpdate = async (itemId: string, newQuantity: number) => {
    setUpdatingQuantity(prev => ({ ...prev, [itemId]: true }));
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    } finally {
      setUpdatingQuantity(prev => ({ ...prev, [itemId]: false }));
    }
  };

  return (
    <UserLayout user={user} setUser={setUser} hideSlider={true}>
      <div className="cart-page-wrapper">
        <div className="cart-container">
          {/* Header */}
          <div className="cart-header-strip">
            <button className="cart-back-btn" onClick={() => navigate(-1)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              <span>Continue Shopping</span>
            </button>
            <div className="cart-title-wrap">
              <h1 className="cart-main-title">Shopping Cart</h1>
              <span className="cart-count-pill">{cart.length} {cart.length === 1 ? 'item' : 'items'}</span>
            </div>
          </div>

          {cart.length === 0 ? (
            <div className="cart-empty-state">
              <div className="cart-empty-icon">🛒</div>
              <h2>Your Shopping Cart is Empty</h2>
              <p>Looks like you haven't added any firecrackers to your cart yet. Explore our festive specials and wholesale combos!</p>
              <button className="cart-shop-now-btn" onClick={() => navigate('/category')}>
                Explore Fireworks &rarr;
              </button>
            </div>
          ) : (
            <>
              {/* Statutory Compliance Notice (Supreme Court 2018 Order) */}
              <StatutoryComplianceNotice
                variant="cart"
                storeName="Jai Bhavani Fireworks"
                phone="+91-7990696364"
                whatsappNumber="917990696364"
              />

              <div className="cart-layout-grid">
              {/* Left Column: Cart Items */}
              <div className="cart-items-column">
                <div className="cart-items-card">
                  <div className="cart-items-card-header">
                    <span>Products in Cart</span>
                    <span className="cart-items-count-text">{cart.reduce((s, i) => s + i.quantity, 0)} Total Quantity</span>
                  </div>

                  <div className="cart-items-list">
                    {cart.map(item => {
                      const resolvedImg = item.imageUrl ? resolveImageUrl(item.imageUrl) : null;
                      const itemSubtotal = item.price * item.quantity;

                      return (
                        <div className="cart-item-row" key={item.id}>
                          <div className="cart-item-media">
                            {resolvedImg ? (
                              <img src={resolvedImg} alt={item.name} loading="lazy" />
                            ) : (
                              <div className="cart-item-placeholder">🧨</div>
                            )}
                          </div>

                          <div className="cart-item-details">
                            <div className="cart-item-name">{item.name}</div>
                            <div className="cart-item-price-tag">
                              ₹{item.price.toFixed(2)} <span className="cart-unit-label">/ unit</span>
                            </div>

                            <div className="cart-item-actions-row">
                              {/* Quantity Stepper */}
                              <div className="cart-qty-stepper">
                                <LoadingButton
                                  onClick={() => handleQuantityUpdate(item.id, item.quantity - 1)}
                                  loading={updatingQuantity[item.id] || false}
                                  disabled={item.quantity <= 1}
                                  className="qty-btn"
                                  aria-label="Decrease quantity"
                                >
                                  -
                                </LoadingButton>
                                <span className="qty-value">{item.quantity}</span>
                                <LoadingButton
                                  onClick={() => handleQuantityUpdate(item.id, item.quantity + 1)}
                                  loading={updatingQuantity[item.id] || false}
                                  className="qty-btn"
                                  aria-label="Increase quantity"
                                >
                                  +
                                </LoadingButton>
                              </div>

                              {/* Item Total */}
                              <div className="cart-item-total-price">
                                ₹{itemSubtotal.toFixed(2)}
                              </div>

                              {/* Remove Button */}
                              <button 
                                onClick={() => removeFromCart(item.id)} 
                                className="cart-item-remove-btn"
                                title="Remove item"
                                aria-label="Remove item from cart"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Order Summary Card */}
              <div className="cart-summary-column">
                <div className="cart-summary-card">
                  <h3 className="summary-title">Order Summary</h3>

                  <div className="summary-lines">
                    <div className="summary-line">
                      <span>Items Total ({cart.reduce((s, i) => s + i.quantity, 0)} items)</span>
                      <span>₹{subtotal.toFixed(2)}</span>
                    </div>

                    <div className="summary-line savings">
                      <span>Wholesale Discount Applied</span>
                      <span>- ₹{totalSavings.toFixed(2)} (50%)</span>
                    </div>

                    <div className="summary-line delivery">
                      <span>Order Fulfillment</span>
                      <span className="free-tag">Offline Booking / Verification</span>
                    </div>

                    <div className="summary-divider"></div>

                    <div className="summary-total-line">
                      <div>
                        <strong>Estimated Total</strong>
                        <span className="tax-subtext">(Inclusive of all taxes)</span>
                      </div>
                      <strong className="summary-total-amount">₹{subtotal.toFixed(2)}</strong>
                    </div>
                  </div>

                  {/* Minimum Order Value Alert / Progress Box */}
                  <div className={`cart-min-order-box ${isMinOrderMet ? 'met' : 'unmet'}`}>
                    <div className="min-order-header">
                      <div className="min-order-title-wrap">
                        <span className="min-order-icon">{isMinOrderMet ? '✅' : '⚡'}</span>
                        <strong>{isMinOrderMet ? 'Wholesale Minimum Met!' : 'Minimum Order: ₹2,000'}</strong>
                      </div>
                      <span className="min-order-badge">
                        {isMinOrderMet ? 'Ready' : `₹${amountNeeded.toFixed(0)} more`}
                      </span>
                    </div>

                    <div className="min-order-progress-track">
                      <div 
                        className="min-order-progress-fill" 
                        style={{ width: `${progressPercent}%` }}
                      ></div>
                    </div>

                    <p className="min-order-hint">
                      {isMinOrderMet 
                        ? '🎉 Minimum wholesale order threshold of ₹2,000 reached.' 
                        : `Add items worth ₹${amountNeeded.toFixed(2)} more to qualify for wholesale direct checkout.`}
                    </p>
                  </div>

                  {/* Ordering Terms Notice */}
                  <div className="cart-terms-notice">
                    <span className="notice-icon">💬</span>
                    <div>
                      <strong>Order via WhatsApp:</strong>
                      <span>After checkout, your order is sent to WhatsApp. A 10% advance confirms your booking.</span>
                    </div>
                  </div>

                  {/* Primary Checkout Button */}
                  <button 
                    className={`cart-checkout-btn ${!isMinOrderMet ? 'disabled' : ''}`} 
                    onClick={() => {
                      if (!isMinOrderMet) {
                        alert(`Minimum order value is ₹${MIN_ORDER_VALUE}. Please add items worth ₹${amountNeeded.toFixed(2)} more to proceed.`);
                        return;
                      }
                      navigate('/checkout');
                    }}
                    disabled={!isMinOrderMet}
                    title={!isMinOrderMet ? `Add ₹${amountNeeded.toFixed(2)} more to enable checkout` : 'Proceed to Checkout'}
                  >
                    <span>{isMinOrderMet ? 'Proceed to Checkout' : `Add ₹${amountNeeded.toFixed(0)} more to Checkout`}</span>
                    <span className="btn-arrow">&rarr;</span>
                  </button>

                  <button 
                    className="cart-continue-btn"
                    onClick={() => navigate('/category')}
                  >
                    + Add More Items
                  </button>
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </UserLayout>
  );
}